import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-ES');
};

const isExpiredDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date(new Date().toDateString());
};

const DomainsPage = () => {
  const token = localStorage.getItem('token');
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [expiredTotal, setExpiredTotal] = useState(0);
  const [expiredOnly, setExpiredOnly] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('expired') === '1'; } catch (_e) { return false; }
  });
  const [filters, setFilters] = useState({ search: '', clientId: '' });
  const [clientOptions, setClientOptions] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [domainForm, setDomainForm] = useState({
    id: null,
    clientId: '',
    dominio: '',
    fechaRegistro: '',
    fechaRenovacion: ''
  });

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  }, []);

  const fetchDomains = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.search) params.append('search', filters.search);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (expiredOnly) params.append('expired', 'true');
      const res = await apiFetch(`/api/domains/all?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo dominios');
      setDomains(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
      setExpiredTotal(Number(data.expiredTotal || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, expiredOnly, token, handleUnauthorized]);

  const fetchClients = useCallback(async (search = '') => {
    setClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      const options = (data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`,
        data: c
      }));
      setClientOptions(options);
    } catch (err) {
      console.error(err.message);
    } finally {
      setClientsLoading(false);
    }
  }, [token, handleUnauthorized]);

  const selectedClient = useMemo(() => {
    if (!filters.clientId) return null;
    const parsed = Number(filters.clientId);
    return clientOptions.find((opt) => opt.value === parsed)
      || { value: parsed, label: `Cliente #${filters.clientId}` };
  }, [filters.clientId, clientOptions]);

  const selectedDomainClient = useMemo(() => {
    if (!domainForm.clientId) return null;
    const parsed = Number(domainForm.clientId);
    return clientOptions.find((opt) => opt.value === parsed)
      || { value: parsed, label: `Cliente #${domainForm.clientId}` };
  }, [domainForm.clientId, clientOptions]);

  const openCreateModal = () => {
    setDomainForm({ id: null, clientId: '', dominio: '', fechaRegistro: '', fechaRenovacion: '' });
    setModalOpen(true);
  };

  const openEditModal = (domain) => {
    setDomainForm({
      id: domain.id,
      clientId: domain.client_id || '',
      dominio: domain.dominio || '',
      fechaRegistro: domain.fecha_registro ? domain.fecha_registro.slice(0, 10) : '',
      fechaRenovacion: domain.fecha_renovacion ? domain.fecha_renovacion.slice(0, 10) : ''
    });
    setModalOpen(true);
  };

  const submitDomain = async (e) => {
    e.preventDefault();
    if (!domainForm.dominio) return alert('Dominio obligatorio');
    if (!domainForm.id && !domainForm.clientId) return alert('Selecciona un cliente');
    setSaving(true);
    try {
      const isEdit = Boolean(domainForm.id);
      const url = isEdit
        ? `/api/clients/domains/${domainForm.id}`
        : `/api/clients/${domainForm.clientId}/domains`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          dominio: domainForm.dominio,
          fechaRegistro: domainForm.fechaRegistro || null,
          fechaRenovacion: domainForm.fechaRenovacion || null,
          clientId: domainForm.clientId || null
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error guardando dominio');
      setModalOpen(false);
      fetchDomains();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteDomain = async (domainId) => {
    if (!window.confirm('¿Eliminar este dominio?')) return;
    try {
      const res = await apiFetch(`/api/clients/domains/${domainId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando dominio');
      fetchDomains();
    } catch (err) {
      alert(err.message);
    }
  };

  const renovar = async (d) => {
    if (!window.confirm(`¿Marcar ${d.dominio} como renovado? Se sumará 1 año a la fecha de renovación.`)) return;
    const base = d.fecha_renovacion ? new Date(d.fecha_renovacion) : new Date();
    base.setFullYear(base.getFullYear() + 1);
    const nuevaRenovacion = base.toISOString().slice(0, 10);
    try {
      const res = await apiFetch(`/api/clients/domains/${d.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          dominio: d.dominio,
          fechaRegistro: d.fecha_registro ? d.fecha_registro.slice(0, 10) : null,
          fechaRenovacion: nuevaRenovacion,
          clientId: d.client_id || null
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo renovar');
      fetchDomains();
    } catch (err) {
      alert(err.message);
    }
  };

  const triggerImport = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiFetch('/api/domains/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error importando dominios');
      alert(`Importación completa. Importados: ${data.imported || 0}. Omitidos: ${data.skipped || 0}.`);
      if (page !== 1) {
        setPage(1);
      } else {
        fetchDomains();
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  useEffect(() => {
    const query = clientQuery.trim();
    const handler = setTimeout(() => {
      fetchClients(query);
    }, 250);
    return () => clearTimeout(handler);
  }, [clientQuery, fetchClients]);

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Dominios</h2>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-primary btn-sm" onClick={triggerImport} disabled={importing}>
            {importing ? 'Importando...' : 'Importar'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={openCreateModal}>Nuevo dominio</button>
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {expiredTotal > 0 && (
        <div className="mb-3">
          <div
            className={`card shadow-sm ${expiredOnly ? 'border-danger' : ''}`}
            style={{ cursor: 'pointer', maxWidth: 340, borderLeft: '4px solid #dc3545' }}
            role="button"
            onClick={() => { setPage(1); setExpiredOnly((v) => !v); }}
          >
            <div className="card-body py-2 d-flex align-items-center gap-3">
              <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>⚠️</span>
              <div>
                <div className="fw-bold text-danger">{expiredTotal} dominio{expiredTotal === 1 ? '' : 's'} caducado{expiredTotal === 1 ? '' : 's'}</div>
                <div className="small text-muted">{expiredOnly ? 'Mostrando solo caducados · clic para ver todos' : 'Clic para ver solo los caducados'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-5">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar dominio"
                value={filters.search}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, search: e.target.value })); }}
              />
            </div>
            <div className="col-md-5">
              <Select
                classNamePrefix="select"
                isLoading={clientsLoading}
                options={clientOptions}
                value={selectedClient}
                placeholder="Filtrar por cliente"
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, clientId: opt ? String(opt.value) : '' })); }}
                onInputChange={(val, action) => {
                  if (action.action === 'input-change') setClientQuery(val);
                  return val;
                }}
                isClearable
                noOptionsMessage={() => 'Sin resultados'}
              />
            </div>
            <div className="col-md-2">
              <button
                className="btn btn-outline-secondary btn-sm w-100"
                type="button"
                onClick={() => { setFilters({ search: '', clientId: '' }); setExpiredOnly(false); setPage(1); }}
              >
                Limpiar
              </button>
            </div>
          </div>

          {loading ? (
            <p>Cargando...</p>
          ) : domains.length ? (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Dominio</th>
                    <th>Registro</th>
                    <th>Renovación</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr key={d.id}>
                      <td>
                        {d.client_id ? (
                          <Link to={`/clientes/${d.client_id}`} className="text-decoration-none">
                            {d.razon_social || `Cliente #${d.client_id}`}
                          </Link>
                        ) : (d.razon_social || '-')}
                      </td>
                      <td>{d.dominio}</td>
                      <td>{formatDate(d.fecha_registro)}</td>
                      <td>
                        <span className={isExpiredDate(d.fecha_renovacion) ? 'text-danger fw-bold' : ''}>{formatDate(d.fecha_renovacion)}</span>
                        {isExpiredDate(d.fecha_renovacion) && <span className="badge bg-danger ms-2">Caducado</span>}
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-success me-2" onClick={() => renovar(d)}>Renovado</button>
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => openEditModal(d)}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteDomain(d.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No hay dominios.</p>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{domainForm.id ? 'Editar dominio' : 'Nuevo dominio'}</h5>
                <button className="btn-close" onClick={() => setModalOpen(false)}></button>
              </div>
              <form onSubmit={submitDomain}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small">Cliente {domainForm.id ? '(opcional)' : '*'}</label>
                    <Select
                      classNamePrefix="select"
                      isLoading={clientsLoading}
                      options={clientOptions}
                      value={selectedDomainClient}
                      placeholder="Selecciona cliente"
                      onChange={(opt) => setDomainForm((p) => ({ ...p, clientId: opt ? String(opt.value) : '' }))}
                      onInputChange={(val, action) => {
                        if (action.action === 'input-change') setClientQuery(val);
                        return val;
                      }}
                      noOptionsMessage={() => 'Sin resultados'}
                      isClearable
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small">Dominio *</label>
                    <input className="form-control" value={domainForm.dominio} onChange={(e) => setDomainForm((p) => ({ ...p, dominio: e.target.value }))} required />
                  </div>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small">Fecha registro</label>
                      <input className="form-control" type="date" value={domainForm.fechaRegistro} onChange={(e) => setDomainForm((p) => ({ ...p, fechaRegistro: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small">Fecha renovación</label>
                      <input className="form-control" type="date" value={domainForm.fechaRenovacion} onChange={(e) => setDomainForm((p) => ({ ...p, fechaRenovacion: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button className="btn btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleImportFile}
      />
    </div>
  );
};

export default DomainsPage;
